<template>
  <AddCommitmentModal
    v-model="backOpen"
    :target="backTarget"
    :deliveries="store.deliveries"
    :mandates="store.commitmentMandates"
    :mandatesLoading="store.commitmentMandatesLoading"
    :openVolumeMwh="store.openMwh"
    :busy="store.busyAction === 'add_commitment'"
    :errorMessage="store.errorOn('back')"
    @search="store.loadCommitmentMandates"
    @submit="onAddCommitment"
  />

  <ResizeSliceModal
    v-model="resizeOpen"
    :slice="activeSlice"
    :busy="resizeBusy"
    :errorMessage="store.errorOn('resize')"
    :mandate="store.resizeMandate"
    @submit="onResize"
  />

  <MoveSliceModal
    v-model="moveOpen"
    :slice="activeSlice"
    :deliveries="store.deliveries"
    :sourceDeliveryId="activeSliceDeliveryId"
    :busy="store.busyAction === 'move'"
    :errorMessage="store.errorOn('move')"
    @submit="onMove"
  />

  <NarrowSliceModal
    v-model="narrowOpen"
    :slice="activeSlice"
    :busy="store.busyAction === 'narrow'"
    :errorMessage="store.errorOn('narrow')"
    @submit="onNarrow"
  />

  <ConfirmationModalComponent
    :visible="isOpen('remove-backing')"
    :title="$t('forwards.deals.remove_backing.title')"
    :message="removeBackingMessage"
    :confirmButtonText="$t('forwards.deals.remove_backing.confirm')"
    confirmButtonClass="btn-danger"
    preserveWhitespace
    :closeOnConfirm="false"
    :busy="store.busyAction === 'delete_backing'"
    :errorMessage="store.errorOn('remove-backing')"
    @confirm="onRemoveBacking"
    @cancel="closeDialog('remove-backing')"
  />
</template>

<script setup lang="ts">
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import {
  formatDateTimeIntoDate,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import AddCommitmentModal, {
  type BackSliceTarget,
} from "@/forwards2/deals/AddCommitmentModal.vue";
import MoveSliceModal from "@/forwards2/deals/MoveSliceModal.vue";
import NarrowSliceModal from "@/forwards2/deals/NarrowSliceModal.vue";
import ResizeSliceModal from "@/forwards2/deals/ResizeSliceModal.vue";
import type { SliceDialogKind } from "@/forwards2/deals/sliceActions";
import { useActiveDialog } from "@/forwards2/deals/useActiveDialog";
import { useDealStore } from "@/forwards2/deals/useDealStore";
import type {
  BeyondStrategyRow,
  SliceResponse,
} from "@/forwards2/forwardsTypes";
import type { PeriodWidth } from "@/forwards2/periodUtils";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The deal page's dialogs that act on one slice of the plan, which the page opens through `open` from a
 * slice row's menu: back open volume, resize, move, narrow, and remove a backing. Each stays up until
 * the backend agrees, so a refusal is read where it was caused.
 */
const emit = defineEmits<{
  /**
   * A commitment was written that its feasibility warnings describe rather than block, so they belong
   * on the page the dialog returns to (RV-feasibility-06).
   */
  warnings: [warnings: string[]];
}>();

const store = useDealStore();
const { t } = useI18n();
const {
  active: activeDialog,
  isOpen,
  open: openDialog,
  close: closeDialog,
  model: dialogModel,
} = useActiveDialog<{ kind: SliceDialogKind; sliceId: number }>();

const backOpen = dialogModel("back");
const resizeOpen = dialogModel("resize");
const moveOpen = dialogModel("move");
const narrowOpen = dialogModel("narrow");

const activeSliceId = computed(() => activeDialog.value?.sliceId ?? null);

const activeSlice = computed<SliceResponse | null>(
  () => store.slices.find((slice) => slice.id === activeSliceId.value) ?? null
);

const activeSliceDeliveryId = computed(
  () =>
    store.deliveries.find((delivery) =>
      delivery.slices.some((slice) => slice.id === activeSliceId.value)
    )?.id ?? null
);

/** The open row the Back menu entry was used on — the add-commitment modal backs exactly that one. */
const backTarget = computed<BackSliceTarget | null>(() => {
  const slice = activeSlice.value;
  const deliveryId = activeSliceDeliveryId.value;
  if (slice === null || deliveryId === null || !slice.is_open) return null;
  return {
    sliceId: slice.id,
    deliveryId,
    periodLabel: slice.period_label,
    volumeMwh: slice.volume_mwh,
  };
});

// Resizing open volume is a one-operation plan save, so it spins under the plan action, not resize.
const resizeBusy = computed(
  () => store.busyAction === "resize" || store.busyAction === "plan"
);

/**
 * What removing a backing costs, spelled out before it happens (user decision 2026-09-16): the
 * volume goes back on the market, and the pull's price snapshot dies with the commitment — wholly
 * when this was its last backed slice, in part otherwise.
 */
const removeBackingMessage = computed(() => {
  const slice = activeSlice.value;
  const commitment = store.commitmentById(slice?.commitment_id ?? null);
  if (slice === null || commitment === null) return "";

  const floor = commitment.minimum_price_snapshot
    ? t("forwards.deals.commitments.floor", {
        price: commitment.minimum_price_snapshot,
      })
    : t("forwards.deals.commitments.no_floor");
  const conditions =
    commitment.contango_condition_snapshot === "none"
      ? floor
      : `${floor} · ${t(`forwards.deals.commitments.contango.${commitment.contango_condition_snapshot}`)}`;

  return [
    t("forwards.deals.remove_backing.pull", {
      date: formatDateTimeIntoDate(commitment.created_at),
      conditions,
    }),
    t(
      commitment.slices.length > 1
        ? "forwards.deals.remove_backing.released_part"
        : "forwards.deals.remove_backing.released_whole",
      {
        mwh: formatNumberWithSpaces(slice.volume_mwh),
        mandate: slice.mandate_name ?? "",
      }
    ),
    t("forwards.deals.remove_backing.snapshot_lost"),
  ].join("\n\n");
});

const open = (kind: SliceDialogKind, sliceId: number) => {
  store.clearActionError();
  openDialog({ kind, sliceId });
  // Only the override needs the mandate's strategy figures.
  const mandateId = activeSlice.value?.mandate_id ?? null;
  if (kind === "resize" && mandateId !== null)
    void store.loadResizeMandate(mandateId);
};

const onAddCommitment = async (
  mandateId: number,
  takes: Record<number, number>,
  ignoreSalesStrategy: boolean
) => {
  const result = await store.addCommitment(
    mandateId,
    takes,
    ignoreSalesStrategy
  );
  if (result === null) return;
  emit("warnings", [
    ...result.warnings,
    ...soldBeyondStrategyNotes(result.beyond_sales_strategy),
  ]);
  closeDialog("back");
};

/** What an override sold beyond the strategy, recalculated at the save, as the page's notes say it. */
const soldBeyondStrategyNotes = (row: BeyondStrategyRow | null): string[] =>
  row === null
    ? []
    : [
        t("forwards.deals.commitments.sold_beyond_strategy", {
          mandate: row.mandate_name,
          mwh: formatNumberWithSpaces(row.volume_mwh),
        }),
      ];

const onRemoveBacking = async () => {
  if (activeSliceId.value === null) return;
  const result = await store.deleteBacking(activeSliceId.value);
  if (result !== null) closeDialog("remove-backing");
};

/** A backed slice's volume is the commitment's and is resized there; open volume is the editor's. */
const onResize = async (volumeMwh: number, ignoreSalesStrategy: boolean) => {
  const slice = activeSlice.value;
  if (slice === null) return;
  const result = await store.resizeCommitment(
    slice.id,
    volumeMwh,
    ignoreSalesStrategy
  );
  if (result === null) return;
  // Growing a slice is an add-commitment from the same mandate, so its feasibility warnings land on
  // the page the same way the add's do (Q-FIX3-01).
  emit("warnings", [
    ...result.resize.warnings,
    ...soldBeyondStrategyNotes(result.beyond_sales_strategy),
  ]);
  closeDialog("resize");
};

const onMove = async (targetDeliveryId: number, volumeMwh: number | null) => {
  if (activeSliceId.value === null) return;
  if (
    (await store.moveSlice(
      activeSliceId.value,
      targetDeliveryId,
      volumeMwh
    )) !== null
  )
    closeDialog("move");
};

const onNarrow = async (width: PeriodWidth) => {
  if (activeSliceId.value === null) return;
  if ((await store.narrowSlice(activeSliceId.value, width)) !== null)
    closeDialog("narrow");
};

defineExpose({ open });
</script>
